/**
 * A constant whose value is a table — the ISO class → ACH table is the one in
 * the register today — shown and edited as a table, never as raw JSON.
 *
 * KAPL (19-Oct-2026), looking at {"ISO 5":240,"ISO 6":90,"ISO 7":6…} in the
 * constants register: "why those iso values are in bracket?". The stored
 * format is unchanged (the same JSON object); only the reading of it is.
 *
 *   summarize(v)        one line of text: "14 classes · ISO 5 240 · ISO 6 90 · …"
 *   <StructuredValue>   that line plus "View table" → a two-column table
 *   <KeyValueEditor>    the table with a number box per row, for an edit
 *   parseKeyValues(d)   the edited rows back to numbers, or the rows that are not
 */

import React, { useState } from "react";

const GREY = "#6B7280";
const LINE = "#E5E7EB";

const isTable = (v) => v !== null && typeof v === "object";
const entriesOf = (v) => (Array.isArray(v) ? v.map((x, i) => [String(i + 1), x]) : Object.entries(v));
const nounOf = (entries) =>
  entries.some(([k]) => /^(ISO|GRADE)\b/i.test(k)) ? "classes" : "entries";
const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : String(+v.toFixed(4));
  if (isTable(v)) return `${entriesOf(v).length} ${nounOf(entriesOf(v))}`;
  return String(v);
};

/** One readable line for any value — a table becomes a count and its first rows. */
export function summarize(v, max = 3) {
  if (!isTable(v)) return fmt(v);
  const e = entriesOf(v);
  if (Array.isArray(v) && e.every(([, x]) => !isTable(x))) return e.map(([, x]) => fmt(x)).join(", ");
  const head = e.slice(0, max).map(([k, x]) => `${k} ${fmt(x)}`).join(" · ");
  return `${e.length} ${nounOf(e)}${head ? ` · ${head}` : ""}${e.length > max ? " · …" : ""}`;
}

const link = { border: 0, background: "none", padding: 0, color: "#2563EB", cursor: "pointer",
               fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap" };
const cell = { padding: "3px 8px", borderBottom: "1px solid #F3F4F6", fontSize: 12 };
const head = { padding: "4px 8px", borderBottom: `1px solid ${LINE}`, fontSize: 10.5, fontWeight: 700,
               color: GREY, textTransform: "uppercase", letterSpacing: 0.3, background: "#F9FAFB" };

function KeyTable({ entries, unit, keyLabel }) {
  return (
    <table style={{ borderCollapse: "collapse", border: `1px solid ${LINE}`, marginTop: 4, background: "#fff" }}>
      <thead><tr>
        <th className="txt" style={head}>{keyLabel}</th>
        <th className="num" style={head}>{unit ? `Value (${unit})` : "Value"}</th>
      </tr></thead>
      <tbody>{entries.map(([k, x]) => (
        <tr key={k}>
          <td className="txt" style={{ ...cell, fontWeight: 600 }}>{k}</td>
          <td className="num" style={cell}>{isTable(x) ? <StructuredValue value={x} /> : fmt(x)}</td>
        </tr>))}</tbody>
    </table>
  );
}

/** A value as it should be read: plain for a number, a summary line with a
 *  "View table" toggle for a table. `open` starts it expanded. */
export function StructuredValue({ value, unit, open: startOpen = false }) {
  const [open, setOpen] = useState(startOpen);
  if (!isTable(value)) return <>{fmt(value)}</>;
  const e = entriesOf(value);
  const noun = nounOf(e);
  return (
    <span style={{ display: "inline-block", whiteSpace: "normal", textAlign: "left" }}>
      <span style={{ whiteSpace: "nowrap" }}>{summarize(value)}</span>{" "}
      <button type="button" style={link} onClick={() => setOpen(o => !o)}>
        {open ? "Hide table" : "View table"}
      </button>
      {open && <KeyTable entries={e} unit={unit} keyLabel={noun === "classes" ? "Class" : "Key"} />}
    </span>
  );
}

/** The edited rows back to numbers. Returns {value} or {error}. */
export function parseKeyValues(rows) {
  const out = {};
  const bad = [];
  for (const [k, v] of Object.entries(rows || {})) {
    const n = typeof v === "number" ? v : Number(String(v).trim());
    if (String(v).trim() === "" || !Number.isFinite(n)) bad.push(k);
    else out[k] = n;
  }
  if (bad.length) return { error: `Enter a number for ${bad.join(", ")}.` };
  return { value: out };
}

/** A key → number table to edit. The keys are fixed — the classes the
 *  register knows; `keys` may name more, offered from a dropdown. `locked`
 *  maps a key to the reason it cannot be edited here (shown, not editable).
 *  `onChange` receives the whole object with the typed strings; turn it back
 *  into numbers with parseKeyValues before saving. */
export function KeyValueEditor({ value, onChange, unit, keys, locked = {}, base, baseLabel = "Current" }) {
  const rows = value || {};
  const missing = (keys || []).filter(k => !(k in rows));
  const [add, setAdd] = useState("");
  const set = (k, v) => onChange({ ...rows, [k]: v });
  return (
    <div>
      <table style={{ borderCollapse: "collapse", border: `1px solid ${LINE}`, background: "#fff" }}>
        <thead><tr>
          <th className="txt" style={head}>Class</th>
          <th className="num" style={head}>{unit ? `Value (${unit})` : "Value"}</th>
          {base && <th className="num" style={head}>{baseLabel}</th>}
          <th className="txt" style={head} />
        </tr></thead>
        <tbody>{Object.entries(rows).map(([k, v]) => {
          const changed = base && k in base && String(base[k]) !== String(v);
          return (
            <tr key={k}>
              <td className="txt" style={{ ...cell, fontWeight: 600 }}>{k}</td>
              <td className="num" style={cell}>
                {locked[k] ? <b>{fmt(v)}</b> : (
                  <input type="number" step="any" value={v ?? ""} onChange={e => set(k, e.target.value)}
                    style={{ width: 90, padding: "2px 6px", fontSize: 12, border: `1px solid ${changed ? "#7C3AED" : LINE}`,
                             borderRadius: 5, boxSizing: "border-box" }} />)}
              </td>
              {base && <td className="num" style={{ ...cell, color: GREY }}>{fmt(base[k])}</td>}
              <td className="txt" style={{ ...cell, color: GREY, fontSize: 11 }}>{locked[k] || (changed ? "changed" : "")}</td>
            </tr>);
        })}</tbody>
      </table>
      {missing.length > 0 && (
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 6 }}>
          <select value={add} onChange={e => setAdd(e.target.value)}
            style={{ fontSize: 12, padding: "2px 6px", border: `1px solid ${LINE}`, borderRadius: 5 }}>
            <option value="">Add a class…</option>
            {missing.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
          <button type="button" style={link} disabled={!add}
            onClick={() => { set(add, base && add in base ? base[add] : ""); setAdd(""); }}>Add</button>
        </div>
      )}
    </div>
  );
}

export { isTable };
